import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Drawer,
    ListItemText,
    List,
    ListItemIcon,
    IconButton,
    Toolbar,
    useTheme,
    Typography,
    Box,
    CssBaseline,
    useMediaQuery,
    ListItemButton,
} from "@mui/material";

import AppBar from '@mui/material/AppBar';

import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import NotificacoesMenu from '../components/Notificacoes/NotificacoesMenu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Groups2 from '@mui/icons-material/Groups2';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import InstallDesktop from '@mui/icons-material/InstallDesktop';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

import { styled } from "@mui/material/styles";
import DashboardUnidades from "./DashboardUnidades";
import GerenciadorClientes from "./GerenciadorClientes";
import InstaladoresDownloads from "./InstaladoresDownloads";
import GerenciadorAtualizacoes from "./GerenciadorAtualizacoes";
import { useLogin } from "../contexts/LoginContext";
import { useColorMode } from "../App";
import { abreviarIniciaisNome } from "../common/utils/funcoes";
import Tooltip from '@mui/material/Tooltip';

interface Menu {
    titulo: string,
    componente: JSX.Element,
    icone: JSX.Element,
    rota: string
}

interface Props {
    window?: () => Window;
}

const drawerWidth = 240;

const BFAppBar = styled(AppBar)({
    position: "fixed",
    boxShadow: "none",
});

const BFGrow = styled('div')({
    flexGrow: 1
});

const BFNotificationButton = styled(IconButton)(({ theme }) => ({
    paddingRight: theme.spacing(4)
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    justifyContent: 'flex-end',
}));

function HomeUnidades(props: Props) {
    const { window } = props;
    const theme = useTheme();
    const colorMode = useColorMode();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [menuAberto, setMenuAberto] = useState(isDesktop);
    const [menuSelecionado, setMenuSelecionado] = useState<JSX.Element | null>(null);
    const [tituloAtivo, setTituloAtivo] = useState<string>('Dashboard');
    const [login] = useLogin();
    const navigate = useNavigate();
    const location = useLocation();

    const menus = useMemo<Array<Menu>>(() => [{
        titulo: 'Dashboard',
        componente: <DashboardUnidades onNavigateToClientes={() => navigate('/clientes')} />,
        icone: <InsertChartOutlinedIcon />,
        rota: '/dashboard'
    },
    {
        titulo: 'Clientes',
        componente: <GerenciadorClientes />,
        icone: <Groups2 />,
        rota: '/clientes'
    },
    {
        titulo: 'Instaladores',
        componente: <InstaladoresDownloads />,
        icone: <InstallDesktop />,
        rota: '/instaladores'
    },
    {
        titulo: 'Atualizações do Agente',
        componente: <GerenciadorAtualizacoes />,
        icone: <SystemUpdateAltIcon />,
        rota: '/atualizacoes'
    }], [navigate]);

    useEffect(() => {
        setMenuAberto(isDesktop);
    }, [isDesktop]);

    const handleDrawerToggle = () => {
        setMenuAberto(!menuAberto);
    };

    useEffect(() => {
        let currentPath = location.pathname;
        if (currentPath === '/') {
            navigate('/dashboard', { replace: true });
            return;
        }

        const matchedMenu = menus.find(m => m.rota === currentPath);
        if (matchedMenu) {
            setMenuSelecionado(matchedMenu.componente);
            setTituloAtivo(matchedMenu.titulo);
        }
    }, [location.pathname, navigate, menus]);

    const cliqueMenu = (menu: Menu) => {
        return () => {
            navigate(menu.rota);
            if (!isDesktop) {
                setMenuAberto(false);
            }
        };
    };

    const container = window !== undefined ? () => window().document.body : undefined;

    return (<Box>
        <CssBaseline />

        <BFAppBar position="static" color="inherit">
            <Toolbar variant="dense"
                sx={{ 
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: '56px'
                }}>
                <BFNotificationButton
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={handleDrawerToggle}>
                    <MenuIcon sx={{ color: theme.palette.text.primary }} />
                </BFNotificationButton>

                <BFGrow />

                <Typography
                    variant="h6"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        caretColor: "transparent",
                        cursor: "pointer"
                    }}
                >
                    Backup Fácil - Unidades
                </Typography>

                <BFGrow />

                <IconButton onClick={colorMode.toggleColorMode} color="inherit" sx={{ mr: 1 }}>
                    {theme.palette.mode === 'dark' ? (
                        <LightModeIcon sx={{ color: theme.palette.text.secondary }} />
                    ) : (
                        <DarkModeIcon sx={{ color: theme.palette.text.secondary }} />
                    )}
                </IconButton>

                <NotificacoesMenu />

                <Tooltip title={login?.usuario?.nome || ""}>
                    <IconButton>
                        <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                            {abreviarIniciaisNome(login?.usuario?.nome)}
                        </Avatar>
                    </IconButton>
                </Tooltip>
            </Toolbar>
        </BFAppBar>

        <Drawer
            sx={{
                width: isDesktop && menuAberto ? drawerWidth : 0,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    border: "none",
                    boxSizing: 'border-box',
                    backgroundColor: theme.palette.background.paper,
                    borderRight: `1px solid ${theme.palette.divider}`
                },
                border: "none"
            }}
            container={container}
            variant={isDesktop ? "persistent" : "temporary"}
            anchor="left"
            open={menuAberto}
            onClose={() => setMenuAberto(false)}
        >
            <DrawerHeader
                sx={{
                    height: "56px",
                    color: theme.palette.text.primary,
                    border: "none",
                    alignItems: "left",
                    justifyContent: "left",            
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>

                <IconButton sx={{
                    alignItems: "left",
                    justifyContent: "left", 
                    color: "inherit"
                }}
                    onClick={handleDrawerToggle}>
                    <MenuIcon />
                </IconButton>
            </DrawerHeader>
            <List sx={{ px: 1 }}>
                {menus.map((menu, index) => {
                    const selecionado = tituloAtivo === menu.titulo;
                    return (
                        <ListItemButton 
                            key={index}
                            onClick={cliqueMenu(menu)}
                            selected={selecionado}
                            sx={{
                                my: 0.5,
                                borderRadius: '8px',
                                px: 2,
                                py: 1,
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(42, 81, 247, 0.15)' : 'rgba(42, 81, 247, 0.08)',
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(42, 81, 247, 0.2)' : 'rgba(42, 81, 247, 0.12)',
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: theme.palette.primary.main,
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                                }
                            }}
                        >
                            <ListItemIcon sx={{ 
                                color: selecionado ? theme.palette.primary.main : theme.palette.text.secondary,
                                minWidth: 36
                            }}>
                                {menu.icone}
                            </ListItemIcon>
                            <ListItemText 
                                primary={menu.titulo} 
                                primaryTypographyProps={{ 
                                    fontWeight: selecionado ? 700 : 500,
                                    fontSize: '0.875rem'
                                }} 
                            />
                        </ListItemButton>
                    );
                })}
            </List>

            {/* Versão do Portal */}
            <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', opacity: 0.6 }}>
                    Portal v2.0.0
                </Typography>
            </Box>
        </Drawer>
        <Box
            component="main"
            sx={
                {
                    flexGrow: 1,
                    p: 3,
                    marginLeft: (menuAberto && isDesktop) ? `${drawerWidth}px` : '0',
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }
            }
        >
            <Box sx={{ paddingTop: "60px", minHeight: `calc(100vh - 50px)` }}>
                {menuSelecionado}
            </Box>
        </Box>
    </Box>);
}

export default HomeUnidades;